import { createConsumer, type Consumer, type Subscription } from "@rails/actioncable";

// The page half of the live API relay (PLAN-live-api-agent.md phases 1 and 1b).
//
// The engine's own relay dials a bare `wss://` URL. Action Cable is not that: it wraps every
// message in a subscribe/message envelope of its own, so a URL cannot reach it. The engine's answer
// is a transport the PAGE builds (engine PLAN-live-api-web.md decision 10), and, since decision 11,
// a PROVIDER that builds one on demand: the engine asks the first time the user opens the LLM
// panel, so a visitor who never opens it costs no socket and no server session.
//
// So this file is a carrier and nothing else. It never reads a frame, never branches on a `type`,
// and never invents one. Frames are JSON text at the engine's edge and JSON objects on the Action
// Cable wire, which is why `send` parses and the subscription's `received` stringifies: one object
// per message in both directions, which is also what makes the server-side row readable.

// The engine's transport and refusal shapes, copied structurally rather than imported. The import
// would be `@polygonjs/js-core/editor`, whose pinned version predates the live API entirely (see the
// pin straddle note in editorInit.ts); a structural copy lets this file compile against the pin and
// still be exactly what the engine expects once the pin moves.
export interface LiveApiRelayTransport {
	send(text: string): void;
	close(): void;
	onMessage(handler: (text: string) => void): void;
	onClose(handler: (reason: string) => void): void;
	onOpen?(handler: () => void): void;
}

/** The one way to decline. Always with a reason: the reason is what the LLM panel draws. */
interface LiveApiRelayRefusal {
	refused: string;
}

type LiveApiRelayProviderResult = LiveApiRelayTransport | LiveApiRelayRefusal;

interface LiveApiRelayTransportOptions {
	sessionToken?: string;
	host?: string;
}

// What this file needs of `controller.liveApi`, which is `undefined` until the editor chunk has
// resolved. `setRelayProvider` arrived after `connectRelayTransport`, so both are optional and the
// fallback below is what keeps an older engine working.
interface LiveApiHandle {
	setRelayProvider?: (
		provider: () => Promise<LiveApiRelayProviderResult>,
		options?: LiveApiRelayTransportOptions,
	) => void;
	clearRelayProvider?: () => void;
	connectRelayTransport?: (
		transport: LiveApiRelayTransport,
		options?: LiveApiRelayTransportOptions,
	) => void;
	disconnectRelay?: () => void;
}
export interface LiveApiRelayHost {
	liveApi?: LiveApiHandle;
}

export interface LiveApiRelayOptions {
	// The controller polygonAppInit returned. `liveApi` is a GETTER on it: the editor chunk resolves
	// after that call returns, so the value has to be read when it is used, never copied at boot.
	controller: LiveApiRelayHost;
	// Whether this page offers a relay at all. False for everyone who is not an admin.
	enabled: boolean;
	// The channel name from the page's own data attribute, so the server names its own channel.
	channel: string;
	// Which scene this tab is on. Undefined on /scenes/new, which has no scene yet and may never
	// save one; the server treats that as a real state rather than a missing value.
	//
	// A GETTER is allowed, and is what the editor passes: a tab that booted on /scenes/new learns its
	// uuid only when the first save creates the record, long after this registration. Either form is
	// read at SUBSCRIBE time, which is when the engine asks for a transport.
	sceneUuid?: string | (() => string | undefined);
}

// The editor chunk resolves after polygonAppInit returns, and a wasm editor takes seconds to
// minutes to boot besides. Same ceiling the engine's own relay puts on its wait for `describe`:
// past it, say so rather than poll a page that is never going to answer.
const HANDLE_POLL_INTERVAL_MS = 250;
const HANDLE_TIMEOUT_MS = 5 * 60_000;

// How long the provider waits for Action Cable to confirm the subscription before giving up. The
// engine holds `connecting` for as long as the provider takes and has no timer of its own (its
// non-goals are explicit: no reconnect, no retry loop, no timer), so a server that never answers
// would otherwise leave the panel saying "connecting" for the rest of the session. The user's retry
// is a button, which asks the provider again.
const SUBSCRIBE_TIMEOUT_MS = 15_000;

let _consumer: Consumer | null = null;
let _subscription: Subscription | null = null;
let _pollTimer: number | undefined;
let _host: LiveApiRelayHost | null = null;

// The scene uuid as the subscription needs it, from either form the host may have given.
function resolveSceneUuid(
	sceneUuid: string | (() => string | undefined) | undefined,
): string | undefined {
	return typeof sceneUuid === "function" ? sceneUuid() : sceneUuid;
}

// Offer this page's relay to the engine. Returns immediately; everything after is asynchronous, and
// nothing is subscribed until the engine asks, which it does when the LLM panel is first drawn.
//
// Silent no-op when the page does not offer one, which is every page that is not an admin's: the
// engine's LLM panel then says "download the app" on its own, which is the intended experience.
export function registerLiveApiRelay(options: LiveApiRelayOptions): void {
	if (!options.enabled || !options.channel) return;
	disconnectLiveApiRelay();
	_host = options.controller;

	const deadline = Date.now() + HANDLE_TIMEOUT_MS;
	const attempt = () => {
		const liveApi = options.controller.liveApi;
		if (liveApi?.setRelayProvider) {
			liveApi.setRelayProvider(() => relayProvide(options), { host: window.location.host });
			return;
		}
		if (liveApi?.connectRelayTransport) {
			// An engine from before the provider existed. Connect eagerly, which is what phase 1 did:
			// the page works, and the only cost is a socket for a panel nobody may open.
			console.info(
				"[live api] this engine has no setRelayProvider, so the relay is connected eagerly",
			);
			void relayProvide(options).then((result) => {
				if ("refused" in result) {
					console.warn(`[live api] the relay was refused: ${result.refused}`);
					return;
				}
				liveApi.connectRelayTransport?.(result, { host: window.location.host });
			});
			return;
		}
		if (Date.now() >= deadline) {
			console.warn("[live api] the editor never exposed a live API, so no relay was offered");
			return;
		}
		_pollTimer = window.setTimeout(attempt, HANDLE_POLL_INTERVAL_MS);
	};
	attempt();
}

// Withdraw the offer and close anything opened under it. Safe to call when none was ever made,
// which is what unloadEditor does on every page that is not an admin's.
export function disconnectLiveApiRelay(): void {
	window.clearTimeout(_pollTimer);
	_pollTimer = undefined;
	// The engine first, so it drops the offer and reports `closed` with a reason instead of noticing
	// a dead carrier.
	_host?.liveApi?.clearRelayProvider?.();
	_host?.liveApi?.disconnectRelay?.();
	_host = null;
	_subscription?.unsubscribe();
	_subscription = null;
	_consumer?.disconnect();
	_consumer = null;
}

// Is this the server saying why it is about to refuse? `{type: "refused", refused: "..."}`, the one
// frame this file reads rather than carries, because it is the one frame that is ABOUT the relay
// rather than sent through it. Both fields are checked so an engine frame that happens to carry a
// `refused` key cannot be mistaken for one.
function isRefusal(data: unknown): data is LiveApiRelayRefusal & { type: "refused" } {
	if (data == null || typeof data !== "object") return false;
	const frame = data as { type?: unknown; refused?: unknown };
	return frame.type === "refused" && typeof frame.refused === "string" && frame.refused !== "";
}

// Subscribe, and answer with the carrier or with the one refusal shape the engine reads. Called by
// the engine, at most once per attempt, the first time the LLM panel is drawn.
function relayProvide(options: LiveApiRelayOptions): Promise<LiveApiRelayProviderResult> {
	return new Promise<LiveApiRelayProviderResult>((resolve) => {
		// Frames that arrive before the engine has registered its handler. The server sends nothing
		// before `hello`, so this should stay empty; it exists because a dropped frame is the thing
		// that makes a wire protocol impossible to debug.
		const pending: string[] = [];
		let onMessage: ((text: string) => void) | undefined;
		let onClose: ((reason: string) => void) | undefined;
		let settled = false;
		let closed = false;

		const settle = (result: LiveApiRelayProviderResult) => {
			if (settled) return;
			settled = true;
			window.clearTimeout(timer);
			resolve(result);
		};
		const closeOnce = (reason: string) => {
			if (closed) return;
			closed = true;
			onClose?.(reason);
		};

		const timer = window.setTimeout(() => {
			subscription.unsubscribe();
			settle({ refused: "the agent service did not answer in time" });
		}, SUBSCRIBE_TIMEOUT_MS);

		_consumer = createConsumer();
		const subscription = _consumer.subscriptions.create(
			{ channel: options.channel, scene_uuid: resolveSceneUuid(options.sceneUuid) },
			{
				connected() {
					// Resolved only once the socket is CONFIRMED, so the transport handed over is already
					// open and needs no `onOpen`: the engine treats a transport without one as ready.
					settle(transport);
				},
				disconnected() {
					// Before the engine has the transport this is a failure to connect; after, it is the
					// carrier going away. Both are answered in the shape the engine is waiting for.
					settle({ refused: "the connection to the agent service dropped" });
					closeOnce("the Action Cable connection dropped");
				},
				rejected() {
					// The server refused the subscription and said nothing about why: a scene this user
					// may not drive, or a frame that never arrived. The sentence below is the fallback.
					// When the server DID say why, `received` has already settled with its words, and
					// `settle` keeps the first answer.
					settle({ refused: "the agent service refused this page" });
					closeOnce("the server refused the live API subscription");
				},
				received(data: unknown) {
					// A refusal frame, which the server sends just before rejecting the subscription
					// (LiveApiChannel#refuse_subscription). It carries the one sentence the person
					// should read: no subscription, or this month's free turns gone. Read only before
					// the transport is handed over, because after that every frame belongs to the
					// engine and a `refused` key inside one is the engine's business, not ours.
					if (!settled && isRefusal(data)) {
						settle({ refused: data.refused });
						closeOnce("the server refused the live API subscription");
						return;
					}
					const text = JSON.stringify(data);
					if (onMessage) onMessage(text);
					else pending.push(text);
				},
			},
		);
		_subscription = subscription;

		const transport: LiveApiRelayTransport = {
			send(text: string) {
				if (closed) return;
				let frame: unknown;
				try {
					frame = JSON.parse(text);
				} catch (error) {
					// The engine builds every frame it sends, so this cannot happen without a bug there.
					// Logged rather than thrown: a carrier that raises into the engine's send path would
					// take the editor down with it.
					console.warn("[live api] a frame from the engine was not valid JSON", error);
					return;
				}
				if (frame == null || typeof frame !== "object") return;
				subscription.send(frame as object);
			},
			close() {
				if (closed) return;
				closeOnce("the page closed the relay");
				subscription.unsubscribe();
			},
			onMessage(handler: (text: string) => void) {
				onMessage = handler;
				for (const text of pending.splice(0)) handler(text);
			},
			onClose(handler: (reason: string) => void) {
				onClose = handler;
			},
		};
	});
}
